/**
 * The branding of the login page before anybody signed in (edge rebuild plan
 * W19): master answers `GET /v1/login/branding` for the hostname the page was
 * asked on — the effective `login` settings of the tenant or company that
 * hostname belongs to, and the public part of its `look`
 * (`backend/master/accountconfig/login_public.go`). Nothing here is a setting
 * and nothing is stored beyond a cache of the last answer; the settings are
 * edited in the settings hub (`settingsHub/LoginPageSettings.vue`).
 *
 * Pure functions plus one fetch, so the page, its preview and the tests share
 * them.
 */

/** The links of the login page. */
export const LOGIN_LINK_NAMES = ['imprint', 'privacy', 'help'] as const;
export type LoginLinkName = (typeof LOGIN_LINK_NAMES)[number];

/** The languages a text of the login page has. */
export const LOGIN_LOCALES = ['de', 'en'] as const;
export type LoginTexts = Partial<Record<(typeof LOGIN_LOCALES)[number], string>>;

/** A background: a colour (`#rrggbb` or the name of a colour token) or an image. */
export interface LoginBackground {
  color?: string;
  image?: string;
}

/** The `login` part of the answer (an image is a link, or a data URI in a preview). */
export interface LoginBrandingLogin {
  logo?: string;
  logo_dark?: string;
  title?: LoginTexts;
  subtitle?: LoginTexts;
  footer?: LoginTexts;
  background?: LoginBackground;
  links?: Partial<Record<LoginLinkName, string>>;
  show_powered_by: boolean;
  methods: string[];
}

export interface LoginBranding {
  login: LoginBrandingLogin;
  /** The public part of the look: `source_scheme`, `seeds`, `tokens`, `type`, `design`. */
  look: Record<string, unknown>;
}

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function text(value: unknown): string | undefined {
  return typeof value === 'string' && value !== '' ? value : undefined;
}

function texts(value: unknown): LoginTexts | undefined {
  if (!isRecord(value)) return undefined;
  const out: LoginTexts = {};
  for (const locale of LOGIN_LOCALES) {
    const one = text(value[locale]);
    if (one) out[locale] = one;
  }
  return Object.keys(out).length ? out : undefined;
}

/** A hex colour or the name of a colour token — nothing a stylesheet could be smuggled into. */
const COLOR = /^(#([0-9a-fA-F]{3,4}|[0-9a-fA-F]{6}|[0-9a-fA-F]{8})|[a-z][a-z0-9-]{0,47})$/;

/** An https link only: the page never renders another scheme as a link. */
function httpsLink(value: unknown): string | undefined {
  const link = text(value);
  if (!link) return undefined;
  try {
    const url = new URL(link);
    return url.protocol === 'https:' && !url.username && !url.password ? url.toString() : undefined;
  } catch {
    return undefined;
  }
}

/** An image the page may show: a link on master or a data URI of a raster image. */
function imageSource(value: unknown): string | undefined {
  const source = text(value);
  if (!source) return undefined;
  if (/^data:image\/(png|webp|jpeg);base64,[A-Za-z0-9+/=]+$/.test(source)) return source;
  if (/^\/v1\/login\/branding\/[0-9a-f]{64}\.(png|webp|jpg)$/.test(source)) return source;
  return undefined;
}

/**
 * Reads master's answer defensively: a field of another shape is left out,
 * the page then shows its default for it. Null when the answer is no
 * branding at all.
 */
export function parseLoginBranding(raw: unknown): LoginBranding | null {
  if (!isRecord(raw) || !isRecord(raw.login)) return null;
  const login = raw.login;
  const out: LoginBrandingLogin = {
    show_powered_by: login.show_powered_by !== false,
    methods: Array.isArray(login.methods)
      ? login.methods.filter((method): method is string => typeof method === 'string')
      : [],
  };
  const logo = imageSource(login.logo);
  if (logo) out.logo = logo;
  const logoDark = imageSource(login.logo_dark);
  if (logoDark) out.logo_dark = logoDark;
  for (const field of ['title', 'subtitle', 'footer'] as const) {
    const value = texts(login[field]);
    if (value) out[field] = value;
  }
  if (isRecord(login.background)) {
    const color = text(login.background.color);
    const image = imageSource(login.background.image);
    if (image) out.background = { image };
    else if (color && COLOR.test(color)) out.background = { color };
  }
  if (isRecord(login.links)) {
    const links: Partial<Record<LoginLinkName, string>> = {};
    for (const name of LOGIN_LINK_NAMES) {
      const link = httpsLink(login.links[name]);
      if (link) links[name] = link;
    }
    if (Object.keys(links).length) out.links = links;
  }
  return { login: out, look: isRecord(raw.look) ? raw.look : {} };
}

/**
 * The branding of a stored `login` document (what the page editor's preview
 * paints, before master served it): images are the data URIs themselves.
 */
export function loginBrandingFromDocument(doc: Record<string, unknown>): LoginBranding {
  const background = text(doc.background);
  const view: Record<string, unknown> = { ...doc };
  if (background?.startsWith('data:')) view.background = { image: background };
  else if (background) view.background = { color: background };
  else delete view.background;
  return parseLoginBranding({ login: view, look: {} }) ?? { login: { show_powered_by: true, methods: [] }, look: {} };
}

/**
 * A text in the page's language, else the other language: a page with only a
 * German title shows it to everybody, an empty one shows the default.
 */
export function loginBrandingText(value: LoginTexts | undefined, locale: string): string | undefined {
  if (!value) return undefined;
  const own = locale.slice(0, 2) as keyof LoginTexts;
  return value[own] ?? value.de ?? value.en;
}

/** The address of a branding image: a link on master is relative to its origin. */
export function loginBrandingImageUrl(source: string, origin: string): string {
  return source.startsWith('/') ? `${origin}${source}` : source;
}

/** The CSS colour of a background colour: a hex value as it is, a token name as the kit's variable. */
export function loginBrandingColor(color: string): string {
  return color.startsWith('#') ? color : `var(--nxt-${color})`;
}

/** The address of the branding on an API origin. */
export function loginBrandingEndpoint(origin: string): string {
  return `${origin}/v1/login/branding`;
}

/** Where the last answer is kept, per API origin, for the next visit's first paint. */
export const LOGIN_BRANDING_CACHE_KEY = 'formica.login.branding';

function cacheKey(origin: string): string {
  return `${LOGIN_BRANDING_CACHE_KEY}.${origin}`;
}

/** The last answer of this origin; null without one. The cache is a convenience, never needed. */
export function readCachedLoginBranding(origin: string): LoginBranding | null {
  try {
    const raw = window.localStorage.getItem(cacheKey(origin));
    return raw ? parseLoginBranding(JSON.parse(raw)) : null;
  } catch {
    return null;
  }
}

function cacheLoginBranding(origin: string, body: unknown): void {
  try {
    window.localStorage.setItem(cacheKey(origin), JSON.stringify(body));
  } catch {
    // A full or blocked store only costs the first paint of the next visit.
  }
}

/**
 * Asks master for the branding of the hostname this page is served on. Null
 * when master is unreachable or answers something else; the page then shows
 * its default look.
 */
export async function fetchLoginBranding(
  origin: string,
  fetchImpl: typeof fetch = fetch,
): Promise<LoginBranding | null> {
  try {
    const response = await fetchImpl(loginBrandingEndpoint(origin), { headers: { Accept: 'application/json' } });
    if (!response.ok) return null;
    const body: unknown = await response.json();
    const branding = parseLoginBranding(body);
    if (branding) cacheLoginBranding(origin, body);
    return branding;
  } catch {
    return null;
  }
}

/**
 * Paints the look the branding carries (the colours, type and shape of the
 * hostname's scope) on the page: the shell's own look is not loaded before
 * anybody signed in. Nothing is stored — signing in reloads the page and the
 * shell paints the account's look. A branding without a look changes nothing.
 */
export async function applyLoginBrandingLook(look: Record<string, unknown>): Promise<void> {
  if (!Object.keys(look).length) return;
  try {
    const { applyLoginLook } = await import('@/settings');
    applyLoginLook(look);
  } catch {
    // The page stays in the platform's own look.
  }
}

/** A branding and the origin that answered it (its image links are relative to that origin). */
export interface LoginBrandingAnswer {
  origin: string;
  branding: LoginBranding;
}

/** The origin this page is served from, when it is a web origin (not a desktop shell's). */
export function pageOrigin(): string | null {
  try {
    const origin = window.location.origin;
    return /^https?:\/\//.test(origin) ? origin : null;
  } catch {
    return null;
  }
}

/**
 * Where to ask for the branding, in order. Master maps the request's Host to a
 * company, and the hostname that counts is the one the page was reached on: a
 * deployment that serves the page itself answers it on the page's own origin,
 * while the API URL the app was given may name the node by its IP. So the
 * page's origin comes first (a page served elsewhere simply does not answer),
 * the server the app talks to second. A server the user chose explicitly is the
 * only candidate (`explicit`), for a desktop app or another installation.
 */
export function loginBrandingOrigins(apiOrigin: string, explicit = false): string[] {
  if (explicit) return [apiOrigin];
  const page = pageOrigin();
  return page && page !== apiOrigin ? [page, apiOrigin] : [apiOrigin];
}

/** The last answer kept for the first of these origins that has one. */
export function readCachedLoginBrandingOf(origins: readonly string[]): LoginBrandingAnswer | null {
  for (const origin of origins) {
    const branding = readCachedLoginBranding(origin);
    if (branding) return { origin, branding };
  }
  return null;
}

/** The branding of the first of these origins that answers one. */
export async function fetchLoginBrandingOf(
  origins: readonly string[],
  fetchImpl: typeof fetch = fetch,
): Promise<LoginBrandingAnswer | null> {
  for (const origin of origins) {
    const branding = await fetchLoginBranding(origin, fetchImpl);
    if (branding) return { origin, branding };
  }
  return null;
}
